import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../ThemeContext';

const MiniGame = () => {
  const canvasRef = useRef(null);
  const [stats, setStats] = useState({ processed: 0, speed: 1 });
  const [isPaused, setIsPaused] = useState(false);
  const particlesRef = useRef([]);
  const processedRef = useRef(0);
  const { isDark } = useTheme();

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = 160;
    const height = 40;
    canvas.width = width;
    canvas.height = height;

    let animationId;

    class DataPoint {
      constructor(startX = -5, startY = null) {
        this.x = startX;
        this.y = startY || Math.random() * (height - 10) + 5;
        this.speed = 0.4 + Math.random() * 0.6;
        this.size = 2 + Math.random() * 2;
        // Different colors for light vs dark mode
        if (isDark) {
          this.color = `hsla(${35 + Math.random() * 15}, ${70 + Math.random() * 20}%, ${55 + Math.random() * 10}%, 0.9)`;
        } else {
          this.color = `hsla(${215 + Math.random() * 15}, ${70 + Math.random() * 20}%, ${45 + Math.random() * 10}%, 0.8)`;
        }
        this.life = 1;
      }

      update() {
        if (isPaused) return;
        
        this.x += this.speed * stats.speed;
        this.life -= 0.002;
        
        // Slight vertical wobble
        this.y += Math.sin(this.x * 0.1) * 0.1;
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.life;
        ctx.shadowBlur = 4;
        ctx.shadowColor = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }

      isDead() {
        return this.x > width + 5 || this.life <= 0;
      }
    }

    const animate = () => {
      // Clear with fade effect - different for light/dark mode
      ctx.fillStyle = isDark ? 'rgba(28, 25, 23, 0.3)' : 'rgba(249, 250, 251, 0.4)';
      ctx.fillRect(0, 0, width, height);

      // Add new data points
      if (!isPaused && Math.random() < 0.03 * stats.speed && particlesRef.current.length < 15) {
        particlesRef.current.push(new DataPoint());
      }

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter(p => {
        p.update();
        p.draw();
        
        if (p.isDead()) {
          if (p.x > width) processedRef.current++;
          return false;
        }
        return true;
      });

      // Update stats
      setStats(s => ({ ...s, processed: processedRef.current }));

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isPaused, stats.speed, isDark]);

  return (
    <div className="relative group">
      <canvas
        ref={canvasRef}
        onDoubleClick={togglePause}
        className="rounded border border-primary-600/30 bg-gray-100 dark:bg-marble-900 cursor-pointer hover:border-primary-500/50 transition-colors"
        style={{ width: '160px', height: '40px' }}
      />
      <div className="absolute -bottom-8 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="flex justify-between text-xs">
          <span className="text-gray-600 dark:text-marble-500">Data Flow</span>
          <span className="text-primary-600 dark:text-primary-400 font-mono">{stats.processed}</span>
        </div>
      </div>
    </div>
  );
};

export default MiniGame;
